import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { finalize, Subject, takeUntil, filter } from 'rxjs';
import { TeamService } from '../../../_core/services/team.service';
import { ToastService } from '../../../_core/services/toast.service';
import { SeasonSelectorService } from '../../../_shared/components/season-selector/season-selector.service';
import { Season } from '../../../_shared/models/season.model';
import { Team } from '../../../_shared/models/team.model';
import { TeamDetailsService } from '../team-details.service';
import { ModalController, NavController } from '@ionic/angular';
import { League } from '../../../_shared/models/league.model';
import { ActivatedRoute } from '@angular/router';
import { PopupCardComponent } from '../../../_shared/components/popup-card/popup-card.component';
import { AuthService } from '../../../auth/auth.service';

@Component({
  standalone: false,
  selector: 'app-team-participants',
  templateUrl: './team-participants.component.html',
  styleUrls: ['./team-participants.component.scss'],
})
export class TeamParticipantsComponent implements OnInit, OnDestroy {
  team: Team;
  isLoading = true;
  selectedSeason: Season;

  private readonly destroy$: Subject<void> = new Subject();
  isOwner: boolean;
  isOfficial: boolean;
  league: League;
  leagueId: string;
  isLeagueITA: boolean;
  teamId: string;
  userId: string;
  organisationId: string | null;
  hasPendingRequest = false;
  pendingRequestsCount = 0;

  constructor(
    private seasonSelectorService: SeasonSelectorService,
    private teamService: TeamService,
    private teamDetailsService: TeamDetailsService,
    private toast: ToastService,
    private navCtrl: NavController,
    private activatedRoute: ActivatedRoute,
    private modalController: ModalController,
    private authService: AuthService
  ) { }

  async ngOnInit() {
    await this.getRouteSubscription();
    this.selectedSeason = this.seasonSelectorService.season;
    await this.subscriptions();
    this.userId = (await this.authService.getAuthUser())._id;
    this.updatePendingRequests();
    this.isLoading = true;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges() {
    this.updatePendingRequests();
  }


  private updatePendingRequests() {
    const participants = this.team?.participants ?? [];
    const pending = participants.filter(
      c => c?.person?.claim_status?.toLowerCase() === 'pending'
    );

    this.pendingRequestsCount = pending.length;
    this.hasPendingRequest = pending.length > 0;
  }

  getRouteSubscription() {
    this.activatedRoute.parent?.params
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (params) => {
          try {
            const { id } = params;
            this.teamId = id;
          } catch (error) {
            console.error('Error fetching route data:', error);
          } finally {
            this.isLoading = false;
          }
        },
        error: (err) => {
          console.error('Route subscription error:', err);
          this.isLoading = false;
        }
      });
  }

  getTeamParticipants() {
    this.isLoading = true;
    //const teamId = this.teamDetailsService?.teamInfo?._id;
    if (this.teamId) {
      this.teamDetailsService
        .getParticiapnts(this.teamId, this.selectedSeason?._id, 'player')
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.isLoading = false))
        .subscribe({
          next: async (res) => {
            this.team = res.data;
            this.teamDetailsService.publishParticipants(res.data.participants);
            await this.getTeamOwnerStatus();
            this.updatePendingRequests();
          }
        });
    }
  }

  isRole(participant, roleType) {
    return participant.roles.some(role => role === roleType);
  }

  removePlayer(playerId, slidingItem: HTMLIonItemSlidingElement) {
    if (!this.isOwner) {
      this.toast.presentToast('You do not have permission to edit or remove this player.');
      return;
    }

    if (this.isOwner &&
      (this.team?.organisations?.[0]?.organisationAbbreviation === 'ITA' || this.team?.organisations?.[0]?.organisationAbbreviation === 'USTA') &&
      !this.team?.organisations?.[0]?.allowRemovePlayer) {
      this.toast.presentToast('Players from ITA-affiliated teams cannot be removed.');
      return;
    }
    this.teamService
      .removePlayer(this.team._id, playerId)
      .subscribe({
        next: () => {
          const index = this.team.participants.findIndex(p => p.person._id === playerId);
          this.team.participants.splice(index, 1);
          // Close the sliding item
          slidingItem.close();
        }
      });
  }

  synchronizeRoster() {
    const teamId = this.teamDetailsService.teamInfo._id;
    this.isLoading = true;

    this.teamDetailsService
      .synchronizeTeamRoster(this.team.teamId, this.selectedSeason?.seasonId, this.team?.organisations[0].organisationAbbreviation)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false))
      .subscribe({
        next: (res) => {
          this.team = res.data;
          this.toast.presentToast(res.message);
        }
      });
  }

  subscriptions() {
    this.seasonSelectorService
      .subscribeSelectedSeason()
      .pipe(
        takeUntil(this.destroy$),
        filter(value => !!value)
      )
      .subscribe(
        (season: Season) => {
          this.selectedSeason = season;
          this.getTeamParticipants()
        }
      );

    this.teamDetailsService
      .subscribeTeamUpdate()
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe(
        (team: Team) => {
          this.team = team;
          this.getTeamOwnerStatus();
        }
      );

    this.teamDetailsService.subscribeUpdateParticipants()
      .pipe(
        takeUntil(this.destroy$),
      ).subscribe(
        () => {
          this.getTeamParticipants();
        }
      );

    this.teamDetailsService.subscribeOrganisationId()
      .pipe(
        takeUntil(this.destroy$),
      ).subscribe((id: string | null) => {
        this.organisationId = id;
        if (!id) {
          this.getTeamParticipants();
        }
      });
  }

  private async getTeamOwnerStatus() {
    this.isOwner = this.teamDetailsService.isOwner(this.team.owners);
    this.isOfficial = await this.teamDetailsService.isOfficial(this.team.officials);
  }

  goToProfilePage(player: any) {
    if (this.team._id) {
      this.navCtrl.navigateRoot(`my-players/${player.person._id}/team/${this.team._id}`);
    } else {
      this.navCtrl.navigateRoot(`my-players/${player.person._id}`);
    }
  }

  hasPendingRequests(): boolean {
    return this.team?.participants?.some(c => c?.person?.claim_status.toLocaleLowerCase() === 'pending') || false;
  }

  getPendingRequestsCount(): number {
    return this.team?.participants?.filter(c => c?.person?.claim_status.toLocaleLowerCase() === 'pending').length;
  }

  nonPendingCoaches() {
    return this.team?.participants?.filter(c => c?.person?.claim_status.toLocaleLowerCase() !== 'pending' && c?.person?.claim_status.toLocaleLowerCase() !== 'verified') || [];
  }

  async approvePlayer(index: number) {
    const claimedBy = this.team?.participants[index]?.person?.claimedBy;
    const participant = this.team?.participants[index]?.person;
    const contactInfo = [
      participant?.claimedBy?.phone ?? '',
      participant?.claimedBy?.email ?? ''
    ].filter(Boolean).join(', ');

    const message = `${claimedBy?.first_name ?? ''} ${claimedBy?.last_name ?? ''}${contactInfo ? ` (${contactInfo})` : ''
      } requests to claim the profile “${participant?.first_name ?? ''} ${participant?.last_name ?? ''}”. Approve to merge all match results under their profile.`;

    const modal = await this.modalController.create({
      component: PopupCardComponent,
      componentProps: {
        title: 'Profile claim',
        message: message,
        primaryLabel: 'Yes, approve',
        secondaryLabel: 'No, decline'
      },
      cssClass: 'dialog-popover',
      showBackdrop: false,   // 🚀 removes the dark backdrop
      backdropDismiss: false // (optional) prevent click outside
    });

    modal.onDidDismiss().then((result) => {
      if (result.data) {
        this.approveOrRejectAdminRequest(this.team?.participants[index]?.person._id ?? '', result?.data?.success ? 'verified' : 'declined');
      }
    });

    await modal.present();
  }

  async revokePlayer(index: number) {
    const participant = this.team?.participants[index]?.person;
    const modal = await this.modalController.create({
      component: PopupCardComponent,
      componentProps: {
        title: 'Remove profile claim',
        message: 'Do you want to remove this profile claim request?',
        primaryLabel: 'Yes, remove',
        secondaryLabel: 'No, keep it'
      },
      cssClass: 'dialog-popover',
      showBackdrop: false,   // 🚀 removes the dark backdrop
      backdropDismiss: false // (optional) prevent click outside
    });

    modal.onDidDismiss().then((result) => {
      if (result?.data?.success) {
        this.revokePlayerRequest(participant?._id);
      }
    });

    await modal.present();
  }

  async revokePlayerRequest(playerId?: string) {
    this.isLoading = true;
    this.teamDetailsService.approveOrRejectStaffRequest(playerId ?? '', this.teamId, this.selectedSeason?._id ?? '', 'player', 'revoked')
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false))
      .subscribe({
        next: async (res) => {
          this.isLoading = false;
          this.getTeamParticipants();
        }
      });
  }


  approveOrRejectAdminRequest(userId: string, status: string) {
    this.isLoading = true;
    this.teamDetailsService.approveOrRejectStaffRequest(userId, this.teamId, this.selectedSeason?._id ?? '', 'player', status)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false))
      .subscribe({
        next: async (res) => {
          this.isLoading = false;
          this.getTeamParticipants();
        }
      });
  }
}
